import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/ui-bits";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function LegalPage({ title, description, path, updated, intro, sections, testid }) {
  usePageMeta({ title: `${title} | 30 NORTH`, description, path });

  return (
    <div data-testid={testid}>
      <section className="border-b border-[#e5e7eb] bg-[#f7f8f9] py-14 sm:py-16" aria-labelledby="legal-heading">
        <div className="container-30 max-w-3xl">
          <Reveal>
            <Eyebrow>Legal</Eyebrow>
            <h1
              id="legal-heading"
              data-testid="legal-headline"
              className="mt-6 text-4xl font-black leading-[1.02] tracking-tight text-[#191c1e] sm:text-5xl"
            >
              {title.toUpperCase()}
            </h1>
            <p className="mt-4 text-sm tracking-wide text-[#5f666d]" data-testid="legal-updated">
              Last updated: {updated}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-14 sm:py-16">
        <div className="container-30 max-w-3xl">
          <Reveal>
            <p className="text-base leading-relaxed text-[#191c1e]/80 sm:text-lg">{intro}</p>
          </Reveal>
          <div className="mt-10 space-y-10">
            {sections.map((section, i) => (
              <Reveal key={section.heading} delay={Math.min(i * 0.03, 0.2)}>
                <div data-testid={`${testid}-section-${i + 1}`}>
                  <h2 className="flex items-baseline gap-4 font-display text-xl font-bold tracking-tight text-[#191c1e]">
                    <span className="text-sm font-extrabold text-[#ff6a00]">{String(i + 1).padStart(2, "0")}</span>
                    {section.heading}
                  </h2>
                  <div className="mt-3 space-y-4 border-l border-[#e5e7eb] pl-6 sm:pl-9">
                    {section.paragraphs.map((p, j) => (
                      <p key={j} className="text-sm leading-relaxed text-[#5f666d] sm:text-base">
                        {p}
                      </p>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-14 border border-[#2b2f33] bg-[#191c1e] p-8 text-white" data-testid="legal-contact-card">
              <h2 className="text-xs font-bold uppercase tracking-[0.22em] text-white/40">Contact Us</h2>
              <p className="mt-5 font-display text-lg font-bold tracking-wide">30 North Transportation Inc.</p>
              <ul className="mt-5 space-y-4 text-sm text-white/70">
                <li className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#ff6a00]" aria-hidden="true" />
                  <span data-testid="legal-address">
                    2907 - 80 Absolute Ave
                    <br />
                    Mississauga, ON L4Z 0A5
                    <br />
                    Canada
                  </span>
                </li>
                <li>
                  <a href="mailto:operations@30north.ca" data-testid="legal-email-link" className="flex items-center gap-3 transition-colors duration-200 hover:text-[#ff6a00]">
                    <Mail className="h-4 w-4 shrink-0 text-[#ff6a00]" aria-hidden="true" />
                    operations@30north.ca
                  </a>
                </li>
                <li>
                  <a href="tel:6138700294" data-testid="legal-phone-link" className="flex items-center gap-3 transition-colors duration-200 hover:text-[#ff6a00]">
                    <Phone className="h-4 w-4 shrink-0 text-[#ff6a00]" aria-hidden="true" />
                    613-870-0294
                  </a>
                </li>
              </ul>
            </div>
            <div className="mt-10">
              <Link
                to="/"
                data-testid="legal-back-home"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap border border-[#191c1e]/25 px-7 py-4 text-[13px] font-bold tracking-[0.12em] text-[#191c1e] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#191c1e]"
              >
                BACK TO HOME
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
